/**
 * Voice AI — Self Healing Logs (Action History)
 * Timestamped list of all fixes/actions with expandable details.
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsService } from '../../services/settings-service.js';
import { liveCallsService } from '../../services/live-calls-service.js';
import { tz } from '../../services/timezone-service.js';

export class SelfHealingLogs extends LitElement {
  static properties = {
    _actions: { state: true },
    _loading: { state: true },
    _page: { state: true },
    _totalPages: { state: true },
    _expandedId: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host { display: block; }

      .actions-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .action-card {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        overflow: hidden;
        transition: border-color var(--transition-fast);
      }
      .action-card:hover {
        border-color: var(--color-border-hover);
      }

      .action-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        cursor: pointer;
        user-select: none;
      }

      .action-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
      }
      .action-dot.fix { background: #3fb950; }
      .action-dot.recommendation { background: #d29922; }
      .action-dot.observation { background: #8b949e; }
      .action-dot.proposal_created { background: #a371f7; }

      .action-time {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        flex-shrink: 0;
        min-width: 80px;
      }

      .action-title {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        flex: 1;
      }

      .action-badge {
        font-family: var(--font-mono);
        font-size: 0.625rem;
        padding: 2px 8px;
        border-radius: var(--radius-sm);
        text-transform: uppercase;
        font-weight: var(--font-semibold);
        flex-shrink: 0;
      }
      .action-badge.fix { background: rgba(63,185,80,0.15); color: #3fb950; }
      .action-badge.recommendation { background: rgba(210,153,34,0.15); color: #d29922; }
      .action-badge.observation { background: rgba(139,148,158,0.15); color: #8b949e; }
      .action-badge.proposal_created { background: rgba(163,113,247,0.15); color: #a371f7; }

      .severity-badge {
        font-family: var(--font-mono);
        font-size: 0.625rem;
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        flex-shrink: 0;
      }
      .severity-badge.critical { background: rgba(248,81,73,0.15); color: #f85149; }
      .severity-badge.high { background: rgba(210,153,34,0.15); color: #d29922; }
      .severity-badge.medium { background: rgba(88,166,255,0.15); color: #58a6ff; }
      .severity-badge.low { background: rgba(139,148,158,0.15); color: #8b949e; }

      .action-expand-icon {
        color: var(--color-text-tertiary);
        transition: transform 0.2s;
        flex-shrink: 0;
      }
      .action-expand-icon.expanded {
        transform: rotate(90deg);
      }

      .action-details {
        padding: 0 var(--space-4) var(--space-4);
        border-top: 1px solid var(--color-border-secondary);
      }

      .detail-row {
        display: flex;
        gap: var(--space-3);
        padding: var(--space-2) 0;
        font-family: var(--font-body);
        font-size: var(--text-xs);
      }
      .detail-label {
        color: var(--color-text-tertiary);
        min-width: 100px;
        flex-shrink: 0;
      }
      .detail-value {
        color: var(--color-text-secondary);
        word-break: break-word;
      }

      .setting-change {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        padding: var(--space-1) var(--space-2);
        background: rgba(88,166,255,0.08);
        border-radius: var(--radius-sm);
        margin-top: var(--space-1);
      }
      .setting-change .old { color: #f85149; text-decoration: line-through; }
      .setting-change .arrow { color: var(--color-text-tertiary); }
      .setting-change .new { color: #3fb950; }

      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--color-text-tertiary);
      }
      .empty-state i {
        font-size: 2rem;
        opacity: 0.3;
        margin-bottom: var(--space-3);
        display: block;
      }

      .pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-3);
        margin-top: var(--space-4);
      }
      .pagination button {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        color: var(--color-text-secondary);
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-sm);
        cursor: pointer;
        font-family: var(--font-body);
        font-size: var(--text-xs);
      }
      .pagination button:hover:not(:disabled) {
        border-color: var(--color-border-hover);
        color: var(--color-text-primary);
      }
      .pagination button:disabled {
        opacity: 0.4;
        cursor: default;
      }
      .pagination .page-info {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }
    `
  ];

  constructor() {
    super();
    this._actions = [];
    this._loading = true;
    this._page = 1;
    this._totalPages = 1;
    this._expandedId = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadActions();
    // Listen for new actions from WebSocket
    this._unsubAction = liveCallsService.onMonitorAction(() => {
      this._loadActions();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._unsubAction) this._unsubAction();
  }

  async _loadActions() {
    this._loading = true;
    try {
      const data = await settingsService.loadMonitorActions(this._page);
      if (data.success) {
        this._actions = data.actions || [];
        this._totalPages = data.pages || 1;
      }
    } catch (err) {
      console.error('[SelfHealingLogs] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _toggleExpand(id) {
    this._expandedId = this._expandedId === id ? null : id;
  }

  _formatTime(isoStr) {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr + 'Z');
      return d.toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: tz.tz
      });
    } catch { return isoStr; }
  }

  _formatDate(isoStr) {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr + 'Z');
      return d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
        timeZone: tz.tz
      });
    } catch { return ''; }
  }

  _goPage(delta) {
    this._page = Math.max(1, Math.min(this._totalPages, this._page + delta));
    this._loadActions();
  }

  render() {
    if (this._loading && this._actions.length === 0) {
      return html`<div class="empty-state"><p>Loading action history...</p></div>`;
    }

    if (this._actions.length === 0) {
      return html`
        <div class="empty-state">
          <i class="fa-sharp fa-light fa-clipboard-list"></i>
          <p>No actions recorded yet. The monitor will log fixes and observations here as it analyzes calls.</p>
        </div>
      `;
    }

    return html`
      <div class="actions-list">
        ${this._actions.map(a => html`
          <div class="action-card">
            <div class="action-header" @click=${() => this._toggleExpand(a.id)}>
              <span class="action-dot ${a.type}"></span>
              <span class="action-time">${this._formatTime(a.created_at)}</span>
              <span class="action-title">${a.title}</span>
              ${a.severity ? html`<span class="severity-badge ${a.severity}">${a.severity}</span>` : ''}
              <span class="action-badge ${a.type}">${a.type}</span>
              <i class="fa-sharp fa-light fa-chevron-right action-expand-icon ${this._expandedId === a.id ? 'expanded' : ''}"></i>
            </div>
            ${this._expandedId === a.id ? html`
              <div class="action-details">
                <div class="detail-row">
                  <span class="detail-label">Summary</span>
                  <span class="detail-value">${a.summary}</span>
                </div>
                ${a.setting_changed ? html`
                  <div class="detail-row">
                    <span class="detail-label">Setting</span>
                    <span class="detail-value">
                      <code>${a.setting_changed}</code>
                      <div class="setting-change">
                        <span class="old">${a.old_value}</span>
                        <span class="arrow">→</span>
                        <span class="new">${a.new_value}</span>
                      </div>
                    </span>
                  </div>
                ` : ''}
                ${a.call_sids ? html`
                  <div class="detail-row">
                    <span class="detail-label">Call SIDs</span>
                    <span class="detail-value" style="font-family:var(--font-mono)">${a.call_sids}</span>
                  </div>
                ` : ''}
                <div class="detail-row">
                  <span class="detail-label">Date</span>
                  <span class="detail-value">${this._formatDate(a.created_at)} ${this._formatTime(a.created_at)}</span>
                </div>
              </div>
            ` : ''}
          </div>
        `)}
      </div>

      ${this._totalPages > 1 ? html`
        <div class="pagination">
          <button @click=${() => this._goPage(-1)} ?disabled=${this._page <= 1}>
            <i class="fa-sharp fa-light fa-chevron-left"></i> Prev
          </button>
          <span class="page-info">${this._page} / ${this._totalPages}</span>
          <button @click=${() => this._goPage(1)} ?disabled=${this._page >= this._totalPages}>
            Next <i class="fa-sharp fa-light fa-chevron-right"></i>
          </button>
        </div>
      ` : ''}
    `;
  }
}

customElements.define('self-healing-logs', SelfHealingLogs);
